/**
 * utils/connectionPool/supabasePool.ts — SupabaseConnectionPool wrapper
 */

import { ConnectionPoolManager } from './manager.js';
import type { PoolConfig } from './types.js';

/**
 * Supabase Connection Pool Wrapper
 * Wraps Supabase client with connection pooling
 */
export class SupabaseConnectionPool {
    private pool: ConnectionPoolManager;
    private supabaseClient: any;
    private activeConnections: Map<string, any> = new Map();

    constructor(supabaseClient: any, poolConfig?: PoolConfig) {
        this.supabaseClient = supabaseClient;
        this.pool = ConnectionPoolManager.getInstance(poolConfig);
    }

    /**
     * Execute query with connection pooling
     */
    async executeWithPool<T>(
        queryFn: (conn: string) => Promise<T>,
        timeout?: number
    ): Promise<T> {
        let connId: string | null = null;

        try {
            connId = await this.pool.acquireConnection();
            this.activeConnections.set(connId, Date.now());

            // Add timeout for query execution
            if (timeout) {
                return await Promise.race([
                    queryFn(connId),
                    new Promise<T>((_, reject) =>
                        setTimeout(() => reject(new Error('Query timeout')), timeout)
                    ),
                ]);
            }

            return await queryFn(connId);
        } finally {
            if (connId) {
                this.pool.releaseConnection(connId);
                this.activeConnections.delete(connId);
            }
        }
    }

    /**
     * Get pool statistics
     */
    getPoolStats() {
        return this.pool.getStats();
    }

    /**
     * Get pool status string
     */
    getPoolStatus(): string {
        return this.pool.getStatus();
    }

    /**
     * Close pool
     */
    close(): void {
        this.pool.close();
        this.activeConnections.clear();
    }
}
